import { ApolloError } from "@apollo/client";

/** sessionStorage key: value is an i18n translation key (not pre-translated text). */
export const NORRE_AUTH_FLASH_STORAGE_KEY = "norre:auth_flash";

/** Shown after forced logout (ErrorLink) + reload; must exist in translation.json. */
export const SESSION_ENDED_RELOGIN_I18N_KEY = "session_ended_relogin";

const NO_AUTH_TOKEN_SUBSTRING = "No authorization token was found";

export function setAuthLogoutFlash(): void {
    try {
        sessionStorage.setItem(
            NORRE_AUTH_FLASH_STORAGE_KEY,
            SESSION_ENDED_RELOGIN_I18N_KEY,
        );
    } catch {
        // ignore quota / private mode
    }
}

export function isAuthRelatedGraphqlMessage(message: string): boolean {
    return (
        message.includes("AnonymousUser") ||
        message.includes("Invalid token") ||
        message.includes("UNAUTHORIZED") ||
        message.includes(NO_AUTH_TOKEN_SUBSTRING)
    );
}

export function isAuthRelatedGraphqlError(err: {
    message: string;
    extensions?: { code?: string };
}): boolean {
    if (isAuthRelatedGraphqlMessage(err.message)) {
        return true;
    }
    const code = err.extensions?.code;
    return code === "UNAUTHENTICATED";
}

/**
 * i18n key for user-visible copy when this GraphQL error matches auth failure.
 * Permission-denied style messages are handled separately in displayGraphqlErrorMessage.
 */
export function userFacingMessageKeyForGraphqlAuthError(err: {
    message: string;
    extensions?: { code?: string };
}): string | null {
    if (!isAuthRelatedGraphqlError(err)) {
        return null;
    }
    return SESSION_ENDED_RELOGIN_I18N_KEY;
}

/**
 * True when the operation never got a usable response from the backend
 * (fetch failed, server unreachable, 5xx) and the failure is not an auth
 * failure. Such errors say nothing about the session's validity.
 */
export function isNonAuthNetworkError(error: unknown): boolean {
    return (
        error instanceof ApolloError &&
        Boolean(error.networkError) &&
        error.graphQLErrors.length === 0 &&
        !isAuthRelatedNetworkError(error.networkError as Error)
    );
}

export function isAuthRelatedNetworkError(networkError: {
    message?: string;
    statusCode?: number;
}): boolean {
    if ("statusCode" in networkError && networkError.statusCode === 401) {
        return true;
    }
    return Boolean(networkError.message?.includes(NO_AUTH_TOKEN_SUBSTRING));
}
